import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useDistanceToTopOfPageMotionValue } from '@/hooks/framer/useDistanceToTopOfPageMotionValue';
import { useMobileTabBarHeightMotionValue } from '@/hooks/framer/useMobileTabBarHeightMotionValue';
import { useScrollYMotionValue } from '@/hooks/framer/useScrollYMotionValue';
import { useViewportHeightMotionValue } from '@/hooks/framer/useViewportHeightMotionValue';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getDynamicFontClass } from '@/hooks/useDynamicFontSize';
import { useIntervalEffect } from '@/hooks/useIntervalEffect';
import { getWindowDevice } from '@/utils/windowDevices';
import clsx from 'clsx';
import { useMotionValueEvent, useScroll, useSpring, useTransform } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
export function useWindowHeroAnimation(windowDeviceVariant, componentId, shouldAnimate, titleText) {
    const hasAnimation = function (hasAnimation, doesntHaveAnimation = undefined) {
        return shouldAnimate ? hasAnimation : doesntHaveAnimation;
    };
    const viewportHeight = useStiffSpring(useViewportHeightMotionValue());
    // Dynamic font sizing only for title
    const titleFontClass = getDynamicFontClass(titleText || '', 'heading-3xl');
    const breakpoint = useBreakpoint();
    const isMobileVariant = breakpoint === 'base' || breakpoint === 'xs';
    const windowDeviceConfig = getWindowDevice(windowDeviceVariant);
    const containerRef = useRef(null);
    const innerContainerRef = useRef(null);
    const [containerAspectRatio, setContainerAspectRatio] = useState();
    const [startAnimationVideo, setStartAnimationVideo] = useState(false);
    const containerAspectRatioOrDefault = containerAspectRatio || 1;
    const windowDeviceAspectRatio = windowDeviceConfig.aspectRatio;
    const clipPathId = `videoClipPath${componentId}`;
    const mobileTabBarHeight = useMobileTabBarHeightMotionValue();
    const containerDistanceToTopOfPage = useDistanceToTopOfPageMotionValue(containerRef);
    const showDebugLines = false;
    useEffect(() => {
        const container = innerContainerRef.current;
        if (!container) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setContainerAspectRatio(entry.contentRect.width / entry.contentRect.height);
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, []);
    const scroll = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });
    const scrollY = useStiffSpring(useScrollYMotionValue());
    const scrollYProgress = useStiffSpring(scroll.scrollYProgress);
    useMotionValueEvent(scrollYProgress, 'change', (value) => {
        if (value >= 0.001)
            setStartAnimationVideo(true);
    });
    const innerContainer = {
        initial: hasAnimation('h-[100vh]', 'px-site-padding'),
        animations: hasAnimation({
            height: useTransform(() => viewportHeight.get() -
                mobileTabBarHeight.get() -
                Math.min(200, Math.max(0, containerDistanceToTopOfPage.get() - scrollY.get()))),
        }, { height: 'auto' }),
    };
    const title = {
        initial: hasAnimation('left-1/2 top-[15%] -translate-x-1/2 -translate-y-1/2 w-full px-1.5 max-w-[min(824px,100%)]'),
        animations: hasAnimation({
            top: useTransform(scrollYProgress, [0, 1], ['15%', '-15%']),
            opacity: useTransform(scrollYProgress, [0, 0.85], [1, 0]),
            scale: useTransform(scrollYProgress, [0, 0.85], [1, 0.85]),
        }, { opacity: 1 }),
    };
    const subTitle = {
        initial: hasAnimation('bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-full px-1.5'),
        animations: hasAnimation({
            bottom: useTransform(scrollYProgress, [0, 0.35, 0.95], ['0%', '0%', '17.5%']),
            opacity: useTransform(scrollYProgress, [0, 0.35, 0.95], [0, 0, 1]),
        }, {
            bottom: 0,
            opacity: 1,
        }),
    };
    const subTitleLeft = {
        animations: hasAnimation({
            translateY: useTransform(scrollYProgress, [0, 0.35, 0.95], ['0%', '0%', '0%']),
            scale: useTransform(scrollYProgress, [0, 0.35, 0.85], [0.85, 0.85, 1]),
        }, {
            translateY: 0,
            scale: 1,
        }),
    };
    const subTitleRight = {
        animations: hasAnimation({
            translateY: useTransform(scrollYProgress, [0, 0.35, 0.95], ['50%', '50%', '0%']),
            scale: useTransform(scrollYProgress, [0, 0.35, 0.95], [0.85, 0.85, 1]),
        }, {
            translateY: 0,
            scale: 1,
        }),
    };
    const video = {
        initial: hasAnimation(clsx('left-1/2 -translate-x-1/2 translate-y-[calc(-50%-0.75rem)]', // TODO: label what 0.75rem is for
        'top-[62.5%]', // title is at 35%, "scroll to explore" is at 90%, so (35 + 90) / 2 = 62.5% from top
        'h-[55%]')),
        animations: hasAnimation({
            clipPath: `url(#${clipPathId})`,
            height: useTransform(scrollYProgress, [0, 1], [
                '55%', // title has top 35%, "scroll to explore" has bottom 10%, so 100 - 35 - 10 = 55% height for video
                '70%', // ending position is top 0, bottom 70%
            ]),
            width: useTransform(scrollYProgress, [0, 1], [
                `${(55 / containerAspectRatioOrDefault) * windowDeviceAspectRatio}%`, // 55% (desired height) * container aspect ratio (to make square) * windowDeviceAspectRatio (to match aspect of window device)
                isMobileVariant ? '108%' : '96%',
            ]),
            top: useTransform(scrollYProgress, [0, 1], [
                '62.5%', // title is at 35%, "scroll to explore" is at 90%, so (35 + 90) / 2 = 62.5% from top
                '35%', // ending position is top 0, bottom 70%, so (70 - 0) / 2 = 35% from top
            ]),
        }, {
            width: '100%',
            height: 'auto',
        }),
    };
    const videoClipPath = {
        d: useTransform(scrollYProgress, [0, 0.25], [windowDeviceConfig.relativePath, windowDeviceConfig.relativePathToSquare]),
        id: clipPathId,
    };
    const scrollToExplore = {
        initial: hasAnimation('bottom-[5%]', // Space is 10% of height, so 5% is middle
        'hidden'),
        animations: hasAnimation({
            opacity: useTransform(scrollYProgress, [0, 0.15], [1, 0]),
            translateY: useTransform(scrollYProgress, [0, 0.15], ['0%', '100%']),
        }),
    };
    const debugLines = showDebugLines ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute top-[35%] z-40 w-full border-t border-red-600" }), _jsx("div", { className: "absolute top-[70%] z-40 w-full border-t border-green-600" }), _jsx("div", { className: "absolute top-[90%] z-40 w-full border-t border-red-600" })] })) : null;
    return {
        containerRef,
        innerContainerRef,
        isReady: typeof containerAspectRatio === 'number',
        innerContainer,
        title,
        subTitle,
        subTitleLeft,
        subTitleRight,
        video,
        videoClipPath,
        scrollToExplore,
        debugLines,
        startAnimationVideo,
        titleFontClass,
    };
}
function useStiffSpring(value) {
    return useSpring(value, { stiffness: 100, damping: 25, restDelta: 0.001 });
}
export function useWindowHeroLineHeightModified() {
    const defaultLineHeight = 1;
    const [lineHeight, setLineHeight] = useState(defaultLineHeight);
    // Interval effect needed to handle race conditions between the browser
    // extensions that apply the line height modifications and this code running.
    useIntervalEffect(1000, () => {
        const element = document.createElement('div');
        element.setAttribute('aria-hidden', 'true');
        element.style.fontSize = '10px';
        element.style.lineHeight = String(defaultLineHeight);
        document.body.appendChild(element);
        const computedStyle = window.getComputedStyle(element);
        const fontSize = parseFloat(computedStyle.fontSize);
        const lineHeight = parseFloat(computedStyle.lineHeight);
        setLineHeight(lineHeight / fontSize);
        return () => element.remove();
    }, []);
    return lineHeight > defaultLineHeight;
}
